<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="900px" persistent>
    <v-card>
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-email-send</v-icon>
        <span class="text-h5">E-Mail Senden</span>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-6">
        <v-form ref="emailForm" v-model="formValid">
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="formData.recipient"
                :items="emailRecipientOptions"
                item-text="label"
                item-value="value"
                label="To*"
                prepend-icon="mdi-account"
                :rules="[rules.required]"
                outlined
                dense
                :hint="selectedRecipientHint"
                persistent-hint
              >
                <template v-slot:selection="{ item }">
                  <v-chip small color="primary" text-color="white">
                    {{ item.label }}
                  </v-chip>
                </template>
              </v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="formData.subject"
                label="Betreff*"
                prepend-icon="mdi-text-subject"
                :rules="[rules.required]"
                outlined
                dense
                counter
                maxlength="200"
              ></v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-select
                v-model="selectedTemplate"
                :items="emailTemplates"
                item-text="name"
                item-value="id"
                label="Vorlage auswählen (optional)"
                prepend-icon="mdi-file-document-outline"
                clearable
                outlined
                dense
                @change="applyTemplate"
                hint="Wählen Sie eine Vorlage, um den E-Mail-Text automatisch auszufüllen"
                persistent-hint
                :loading="loadingTemplates"
              >
                <template v-slot:item="{ item }">
                  <v-list-item-icon>
                    <v-icon>mdi-file-document</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="formData.body"
                label="E-Mail Text*"
                prepend-icon="mdi-text"
                rows="12"
                :rules="[rules.required]"
                outlined
                counter
                auto-grow
                :hint="`${formData.body.length} characters`"
                persistent-hint
              >
                <template v-slot:append>
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        @click="clearBody"
                      >
                        <v-icon small>mdi-eraser</v-icon>
                      </v-btn>
                    </template>
                    <span>Clear body</span>
                  </v-tooltip>
                </template>
              </v-textarea>
            </v-col>
          </v-row>

          <v-alert
            v-if="showValidationError"
            type="error"
            dense
            outlined
            dismissible
            @input="showValidationError = false"
          >
            Bitte füllen Sie alle erforderlichen Felder aus, bevor Sie die E-Mail senden.
          </v-alert>

          <!-- Info about mailto limitations -->
          <v-alert
            type="info"
            dense
            outlined
            class="mt-4"
          >
            <div class="d-flex align-center">
              <v-icon small left>mdi-information</v-icon>
              <span>Die E-Mail wird in Ihrem Standard-E-Mail-Programm (z.B. Thunderbird) geöffnet. Anhänge können nicht per mailto-Link übermittelt werden.</span>
            </div>
          </v-alert>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-chip small outlined>
          <v-icon left small>mdi-information</v-icon>
          {{ customer ? customer.company : 'No customer selected' }}
        </v-chip>
        <v-spacer></v-spacer>
        <v-btn text @click="close" class="mr-2">
          <v-icon left>mdi-cancel</v-icon>
          Abbrechen
        </v-btn>
        <v-btn 
          color="primary" 
          @click="openInEmailClient"
          :disabled="!formValid"
          :href="mailtoLink"
          target="_blank"
        >
          <v-icon left>mdi-email-fast-outline</v-icon>
          In E-Mail-Client öffnen
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'EmailDialog',

  props: {
    value: Boolean,
    customer: Object,
    contactPersons: {
      type: Array,
      default: () => []
    },
    contact: Object
  },

  data() {
    return {
      formValid: false,
      showValidationError: false,
      loadingTemplates: false,
      selectedTemplate: null,
      emailTemplates: [],
      
      formData: {
        recipient: '',
        subject: '',
        body: ''
      },

      rules: {
        required: value => !!value || 'Dieses Feld ist erforderlich.',
      }
    }
  },

  computed: {
    emailRecipientOptions() {
      const options = []
      
      if (this.customer && this.customer.email) {
        options.push({
          label: `Company: ${this.customer.email}`,
          value: this.customer.email
        })
      }
      
      if (this.contactPersons && this.contactPersons.length > 0) {
        this.contactPersons.forEach(contact => {
          if (contact.email) {
            options.push({
              label: `${contact.name}: ${contact.email}`,
              value: contact.email
            })
          }
        })
      }
      
      return options
    },

    selectedRecipientHint() {
      if (!this.formData.recipient) return 'Wählen Sie einen E-Mail-Empfänger aus'
      return `Empfänger: ${this.formData.recipient}`
    },

    mailtoLink() {
      if (!this.formValid) return '#'
      
      const subject = encodeURIComponent(this.formData.subject)
      const body = encodeURIComponent(this.formData.body)
      
      return `mailto:${this.formData.recipient}?subject=${subject}&body=${body}`
    }
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.resetForm()
        this.showValidationError = false
        this.loadEmailTemplates()
      }
    },
    
    contact: {
      immediate: true,
      handler(newContact) {
        if (newContact && newContact.email) {
          this.formData.recipient = newContact.email
        }
      }
    }
  },

  methods: {
    resetForm() {
      this.formData = {
        recipient: this.contact?.email || '',
        subject: '',
        body: ''
      }
      this.selectedTemplate = null
      
      if (this.$refs.emailForm) {
        this.$refs.emailForm.resetValidation()
      }
    },

    applyTemplate() {
      if (this.selectedTemplate) {
        const template = this.emailTemplates.find(t => t.id === this.selectedTemplate)
        if (template && template.content) {
          this.formData.body = template.content.replace(/<br>/g, '\n')
        }
      }
    },

    clearBody() {
      this.formData.body = ''
      this.selectedTemplate = null
    },

    async openInEmailClient() {
      if (!this.formValid) {
        this.showValidationError = true
        return
      }
      await this.createEmailNote()

      // The actual navigation happens via the href in the button
      // We can close the dialog after a short delay
      setTimeout(() => {
        this.close()
      }, 500)
    },
 async createEmailNote() {
      try {
        const username = localStorage.getItem('username')
        const currentTime = new Date().toISOString()

        const param = {
          table: "conversation",
          statement: "insert",
          type: "",
          where: "",
          selector: "",
          value: "",
          short: "false",
          extension: ""
        }

        
        const noteText = `E-Mail gesendet an: ${this.formData.recipient}[NEWLINE]Betreff: ${this.formData.subject}[NEWLINE][NEWLINE]${this.formData.body.replace(/\n/g, '[NEWLINE]')}`

        const conversationData = {
          customer_id: this.customer.id,
          conversation_type: 'email',
          note: noteText,
          created_by: username,
          created_at: currentTime,
          updated_at: currentTime
        }

        await this.$func.queryAPI(param, conversationData)

        // Update customer's last conversation timestamp
        const updateParam = {
          table: "customer",
          statement: "update",
          where: "true",
          selector: "id",
          value: this.customer.id
        }

        await this.$func.queryAPI(updateParam, {
          lastConversation: currentTime
        })

        console.log('Email note created successfully')
        this.$emit('refresh')

        // Show success message
        this.$store.dispatch('showSnackbar', {
          message: 'E-Mail-Notiz wurde erfolgreich erstellt',
          color: 'success'
        })

      } catch (error) {
        console.error('Error creating email note:', error)
        this.$store.dispatch('showSnackbar', {
          message: 'Fehler beim Erstellen der E-Mail-Notiz',
          color: 'error'
        })
      }
    },
    close() {
      this.$emit('input', false)
    },

    async loadEmailTemplates() {
      this.loadingTemplates = true
      try {
        const param = {
          table: "email_templates",
          statement: "select", 
          type: "list",
          where: "false",
          selector: "",
          value: "",
          short: "false",
          extension: ""
        }
        
        const data = {}
        
        const result = await this.$func.queryAPI(param, data)
        
        console.log("Loaded email templates:", result)
        
        this.emailTemplates = result.map(template => ({
          id: template.id,
          name: template.name || 'Unnamed Template',
          content: template.content || ''
        }))
        
      } catch (error) {
        console.error('Error loading email templates:', error)
        this.emailTemplates = []
      } finally {
        this.loadingTemplates = false
      }
    }
  }
}
</script>

<style scoped>
.v-card__title {
  position: sticky;
  top: 0;
  z-index: 1;
}
</style>